import { useAtom } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { inputModeAtom, isPanningAtom, isZoomingAtom, presentationBoundsAtom, presentationPositionAtom, presentationViewChangedAtom, presentationZoomAtom, stageRefAtom, stageViewportAtom, } from '@/store/boardAtoms';
import { clampZoom } from '@/utils/board/boardUtils';
import { endFastPan, startFastPan } from '@/utils/board/panZoomOptimization';
import { isElementShape } from '@/utils/board/stageUtils';
import { getOS, OSTypeEnum } from '@/utils/detectOS';
import { calculatePresentationTransform } from '@/utils/presentation/utils';
export const useStageTouchEvents = ({ selectedIds: _selectedIds, isPresentationMode = false, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Получаем setter функции из атома
    const [, setViewport] = useAtom(stageViewportAtom);
    const setZoom = useCallback((zoom) => {
        setViewport((prev) => {
            const newZoom = typeof zoom === 'function' ? zoom(prev.zoom) : zoom;
            return Object.assign(Object.assign({}, prev), { zoom: newZoom });
        });
    }, [setViewport]);
    const setPosition = useCallback((position) => {
        setViewport((prev) => {
            const newPosition = typeof position === 'function' ? position(prev.position) : position;
            return Object.assign(Object.assign({}, prev), { position: newPosition });
        });
    }, [setViewport]);
    const [, setIsZooming] = useAtom(isZoomingAtom);
    const [isPanning, setIsPanning] = useAtom(isPanningAtom);
    const [inputMode] = useAtom(inputModeAtom);
    // Атомы для режима презентации
    const [presentationBounds] = useAtom(presentationBoundsAtom);
    const [presentationZoom, setPresentationZoom] = useAtom(presentationZoomAtom);
    const [, setPresentationPosition] = useAtom(presentationPositionAtom);
    const [, setPresentationViewChanged] = useAtom(presentationViewChangedAtom);
    const debounceIsPanningRef = useRef(null);
    const debounceIsZoomingRef = useRef(null);
    // Refs для оптимизации производительности на мобильных устройствах
    const rafUpdateRef = useRef(null);
    const pendingZoomRef = useRef(null);
    const pendingPositionRef = useRef(null);
    const containerRectRef = useRef(null);
    const containerRectUpdateTimeRef = useRef(0);
    const os = getOS();
    // Константы
    const TOUCH_PAN_SPEED = 1.2;
    const TRACKPAD_PAN_SPEED = 1.8;
    const ZOOM_THRESHOLD = 10;
    const PAN_THRESHOLD = 2;
    const CONTAINER_RECT_CACHE_MS = 100;
    const MOUSE_ZOOM_THRESHOLD = 45;
    const TRACKPAD_ZOOM_SENSITIVITY = 0.01;
    const MOUSE_ZOOM_SCALE = 1.1;
    // ==========================
    // ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
    // ==========================
    // Отслеживаем изменения размеров экрана/фуллскрина для корректного перерасчёта смещений презентации
    const [screenSize, setScreenSize] = useState(() => ({
        width: window.innerWidth,
        height: window.innerHeight,
    }));
    useEffect(() => {
        const handleResize = () => {
            setScreenSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', handleResize);
        document.addEventListener('fullscreenchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('fullscreenchange', handleResize);
        };
    }, []);
    // Cleanup для RAF при размонтировании
    useEffect(() => {
        return () => {
            if (rafUpdateRef.current !== null) {
                cancelAnimationFrame(rafUpdateRef.current);
                rafUpdateRef.current = null;
            }
        };
    }, []);
    /**
     * Возвращает коэффициенты масштаба и смещения для презентационного режима
     * относительно размеров экрана. Для обычного режима возвращает единичные значения.
     */
    const presentationTransform = useMemo(() => {
        if (!presentationBounds) {
            return { scaleX: 1, offsetX: 0, offsetY: 0 };
        }
        const { scaleX, offsetX, offsetY } = calculatePresentationTransform(presentationBounds, screenSize);
        return { scaleX, offsetX, offsetY };
    }, [presentationBounds, screenSize]);
    /**
     * Вычисляет обновления зума и позиции вокруг точки pointer с учётом режима.
     */
    const computeZoomUpdate = useCallback((stage, zoomBase, zoomFactor, pointer) => {
        const newZoom = clampZoom(zoomBase * zoomFactor);
        const oldScale = stage.scaleX();
        const mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale,
        };
        if (isPresentationMode) {
            const { scaleX, offsetX, offsetY } = presentationTransform;
            const newScale = newZoom * scaleX;
            return {
                newZoom,
                newPosition: {
                    x: pointer.x - mousePointTo.x * newScale - offsetX,
                    y: pointer.y - mousePointTo.y * newScale - offsetY,
                },
            };
        }
        return {
            newZoom,
            newPosition: {
                x: pointer.x - mousePointTo.x * newZoom,
                y: pointer.y - mousePointTo.y * newZoom,
            },
        };
    }, [isPresentationMode, presentationTransform]);
    /**
     * Получает кэшированный getBoundingClientRect для оптимизации производительности.
     */
    const getCachedContainerRect = useCallback((stage) => {
        const now = Date.now();
        if (!containerRectRef.current ||
            now - containerRectUpdateTimeRef.current > CONTAINER_RECT_CACHE_MS) {
            containerRectRef.current = stage.container().getBoundingClientRect();
            containerRectUpdateTimeRef.current = now;
        }
        return containerRectRef.current;
    }, []);
    /**
     * Получает текущий zoom со Stage с учётом режима презентации
     */
    const getCurrentZoom = useCallback((stage) => {
        return isPresentationMode ? stage.scaleX() / presentationTransform.scaleX : stage.scaleX();
    }, [isPresentationMode, presentationTransform.scaleX]);
    /**
     * Преобразует позицию Stage в координаты для атомов с учётом режима презентации
     */
    const transformPositionForAtoms = useCallback((position) => {
        if (isPresentationMode) {
            const { offsetX, offsetY } = presentationTransform;
            return { x: position.x - offsetX, y: position.y - offsetY };
        }
        return position;
    }, [isPresentationMode, presentationTransform]);
    /**
     * Обновляет атомы с учётом режима (общая логика для синхронного и асинхронного обновления)
     */
    const updateAtoms = useCallback((zoom, position, stage) => {
        const finalZoom = zoom !== null && zoom !== void 0 ? zoom : getCurrentZoom(stage);
        const finalPosition = position !== null && position !== void 0 ? position : transformPositionForAtoms(stage.position());
        if (isPresentationMode) {
            setPresentationZoom(finalZoom);
            setPresentationPosition(finalPosition);
            setPresentationViewChanged(true);
        }
        else {
            // Используем единый стейт для обычного режима
            setZoom(finalZoom);
            setPosition(finalPosition);
        }
    }, [
        getCurrentZoom,
        transformPositionForAtoms,
        isPresentationMode,
        setZoom,
        setPosition,
        setPresentationZoom,
        setPresentationPosition,
        setPresentationViewChanged,
    ]);
    /**
     * Применяет панорамирование для трекпада (wheel события)
     */
    const handleTrackpadPan = useCallback((stage, deltaX, deltaY) => {
        setIsPanning(true);
        // Оптимизируем pan операцию
        startFastPan(stage);
        const currentPosition = stage.position();
        const newPosition = {
            x: currentPosition.x - deltaX * TRACKPAD_PAN_SPEED,
            y: currentPosition.y - deltaY * TRACKPAD_PAN_SPEED,
        };
        stage.position(newPosition);
        updateAtoms(null, transformPositionForAtoms(newPosition), stage);
        endFastPan();
        if (debounceIsPanningRef.current)
            clearTimeout(debounceIsPanningRef.current);
        debounceIsPanningRef.current = setTimeout(() => setIsPanning(false), 400);
    }, [setIsPanning, transformPositionForAtoms, updateAtoms]);
    /**
     * Обрабатывает zoom для мыши (wheel события)
     */
    const handleMouseZoom = useCallback((stage, pointer, direction) => {
        if (isPanning || touchStateRef.current.isPanning)
            return;
        // Оптимизируем zoom операцию
        startFastPan(stage);
        const zoomFactor = direction > 0 ? MOUSE_ZOOM_SCALE : 1 / MOUSE_ZOOM_SCALE;
        const currentViewport = store.get(stageViewportAtom);
        const baseZoom = isPresentationMode ? presentationZoom : currentViewport.zoom;
        const { newZoom, newPosition } = computeZoomUpdate(stage, baseZoom, zoomFactor, pointer);
        updateAtoms(newZoom, newPosition, stage);
        endFastPan();
    }, [isPanning, isPresentationMode, presentationZoom, computeZoomUpdate, updateAtoms]);
    /**
     * Обрабатывает zoom для трекпада (wheel события с ctrl)
     */
    const handleTrackpadZoom = useCallback((stage, deltaY, pointer) => {
        if (isPanning || touchStateRef.current.isPanning)
            return;
        setIsZooming(true);
        // Оптимизируем zoom операцию
        startFastPan(stage);
        const scaleFactor = Math.exp(-deltaY * TRACKPAD_ZOOM_SENSITIVITY);
        const baseZoom = getCurrentZoom(stage);
        const { newZoom, newPosition } = computeZoomUpdate(stage, baseZoom, scaleFactor, pointer);
        updateAtoms(newZoom, newPosition, stage);
        endFastPan();
        if (debounceIsZoomingRef.current)
            clearTimeout(debounceIsZoomingRef.current);
        debounceIsZoomingRef.current = setTimeout(() => setIsZooming(false), 200);
    }, [isPanning, getCurrentZoom, computeZoomUpdate, updateAtoms, setIsZooming]);
    /**
     * Батчит обновления атомов через requestAnimationFrame для оптимизации производительности.
     */
    const scheduleAtomUpdate = useCallback(() => {
        if (rafUpdateRef.current !== null)
            return;
        rafUpdateRef.current = requestAnimationFrame(() => {
            const stage = store.get(stageRefAtom);
            if (!stage) {
                rafUpdateRef.current = null;
                return;
            }
            updateAtoms(pendingZoomRef.current, pendingPositionRef.current, stage);
            pendingZoomRef.current = null;
            pendingPositionRef.current = null;
            rafUpdateRef.current = null;
        });
    }, [store, updateAtoms]);
    /**
     * Возвращает позицию указателя относительно контейнера Stage из WheelEvent
     */
    const getPointerFromWheelEvent = useCallback((stage, e) => {
        const rect = getCachedContainerRect(stage);
        return {
            x: e.evt.clientX - rect.left,
            y: e.evt.clientY - rect.top,
        };
    }, [getCachedContainerRect]);
    // Состояние для отслеживания touch жестов
    const touchStateRef = useRef({
        isPanning: false,
        isZooming: false,
        isZoomingFlagSet: false,
        gestureType: null,
        lastTouchCount: 0,
        lastTouchPositions: [],
        lastDistance: 0,
        lastCenter: { x: 0, y: 0 },
        initialZoom: 1,
        initialPosition: { x: 0, y: 0 },
        initialDistance: 0,
        initialCenter: { x: 0, y: 0 },
    });
    /**
     * Вычисляет расстояние между двумя точками
     */
    const getDistance = useCallback((p1, p2) => {
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        return Math.sqrt(dx * dx + dy * dy);
    }, []);
    /**
     * Вычисляет центр между двумя точками
     */
    const getCenter = useCallback((p1, p2) => {
        return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
    }, []);
    /**
     * Обновляет позицию и масштаб Stage с учётом режима презентации
     */
    const updateStageTransform = useCallback((stage, zoom, position) => {
        if (isPresentationMode) {
            const { scaleX, offsetX, offsetY } = presentationTransform;
            stage.scaleX(zoom * scaleX);
            stage.scaleY(zoom * scaleX);
            stage.position({ x: position.x + offsetX, y: position.y + offsetY });
        }
        else {
            stage.scaleX(zoom);
            stage.scaleY(zoom);
            stage.position(position);
        }
    }, [isPresentationMode, presentationTransform]);
    /**
     * Сбрасывает флаг зума, если он был установлен
     */
    const resetZoomFlag = useCallback(() => {
        touchStateRef.current.isZoomingFlagSet = false;
        if (touchStateRef.current.isZooming) {
            setIsZooming(false);
            touchStateRef.current.isZooming = false;
        }
    }, [setIsZooming]);
    /**
     * Инициализирует состояние multi-touch жеста
     */
    const initializeMultiTouchGesture = useCallback((stage, pos1, pos2) => {
        const initialDistance = getDistance(pos1, pos2);
        const initialCenter = getCenter(pos1, pos2);
        const currentViewport = store.get(stageViewportAtom);
        const currentZoom = isPresentationMode ? getCurrentZoom(stage) : currentViewport.zoom;
        touchStateRef.current.lastTouchPositions = [pos1, pos2];
        touchStateRef.current.lastDistance = initialDistance;
        touchStateRef.current.lastCenter = initialCenter;
        touchStateRef.current.initialDistance = initialDistance;
        touchStateRef.current.initialCenter = initialCenter;
        touchStateRef.current.initialZoom = currentZoom;
        touchStateRef.current.initialPosition = { x: stage.x(), y: stage.y() };
        touchStateRef.current.isPanning = true;
        touchStateRef.current.isZooming = false;
        touchStateRef.current.gestureType = null;
    }, [getDistance, getCenter, isPresentationMode, getCurrentZoom]);
    /**
     * Определяет тип жеста на основе изменений расстояния и центра
     */
    const detectGestureType = useCallback((distanceChange, centerChange, currentGestureType) => {
        const CONTINUE_GESTURE_THRESHOLD = 0.5;
        // Если жест уже определен, продолжаем его с меньшим порогом
        if (currentGestureType === 'zoom') {
            return distanceChange > CONTINUE_GESTURE_THRESHOLD ? 'zoom' : null;
        }
        if (currentGestureType === 'pan') {
            return centerChange > CONTINUE_GESTURE_THRESHOLD ? 'pan' : null;
        }
        // Определяем новый жест: zoom имеет приоритет если изменение расстояния больше
        if (distanceChange > centerChange && distanceChange > ZOOM_THRESHOLD) {
            return 'zoom';
        }
        if (centerChange > PAN_THRESHOLD) {
            return 'pan';
        }
        return null;
    }, []);
    /**
     * Обновляет состояние жеста после обработки
     */
    const updateGestureState = useCallback((pos1, pos2) => {
        touchStateRef.current.lastTouchPositions = [pos1, pos2];
        touchStateRef.current.lastDistance = getDistance(pos1, pos2);
        touchStateRef.current.lastCenter = getCenter(pos1, pos2);
    }, [getDistance, getCenter]);
    /**
     * Обрабатывает zoom жест для touch событий
     * Использует относительное изменение для плавного зума от текущего состояния
     */
    const handleTouchZoom = useCallback((stage, scaleFactor, center) => {
        if (!touchStateRef.current.isZoomingFlagSet) {
            setIsZooming(true);
            touchStateRef.current.isZoomingFlagSet = true;
            // Начинаем оптимизацию при первом вызове
            startFastPan(stage);
        }
        touchStateRef.current.isZooming = true;
        // Используем текущий зум со Stage для более точного вычисления
        const baseZoom = getCurrentZoom(stage);
        const rect = getCachedContainerRect(stage);
        const pointer = { x: center.x - rect.left, y: center.y - rect.top };
        // Используем существующую функцию для правильного вычисления позиции
        const { newZoom, newPosition } = computeZoomUpdate(stage, baseZoom, scaleFactor, pointer);
        updateStageTransform(stage, newZoom, newPosition);
        pendingZoomRef.current = newZoom;
        pendingPositionRef.current = newPosition;
        scheduleAtomUpdate();
        // Обновляем оптимизацию при каждом движении
        endFastPan();
    }, [
        getCurrentZoom,
        getCachedContainerRect,
        computeZoomUpdate,
        updateStageTransform,
        scheduleAtomUpdate,
        setIsZooming,
    ]);
    /**
     * Обрабатывает pan жест для touch событий
     */
    const handleTouchPan = useCallback((stage, deltaX, deltaY) => {
        resetZoomFlag();
        // Начинаем оптимизацию при первом pan движении
        if (!touchStateRef.current.isPanning) {
            touchStateRef.current.isPanning = true;
            startFastPan(stage);
        }
        const currentPosition = stage.position();
        const newPosition = {
            x: currentPosition.x + deltaX * TOUCH_PAN_SPEED,
            y: currentPosition.y + deltaY * TOUCH_PAN_SPEED,
        };
        stage.position(newPosition);
        pendingZoomRef.current = getCurrentZoom(stage);
        pendingPositionRef.current = transformPositionForAtoms(newPosition);
        scheduleAtomUpdate();
        // Обновляем оптимизацию при каждом движении
        endFastPan();
    }, [resetZoomFlag, getCurrentZoom, transformPositionForAtoms, scheduleAtomUpdate]);
    /**
     * Финализирует обновления атомов синхронно (для использования в конце жеста)
     */
    const finalizeAtomUpdates = useCallback((stage) => {
        if (rafUpdateRef.current !== null) {
            cancelAnimationFrame(rafUpdateRef.current);
            rafUpdateRef.current = null;
        }
        updateAtoms(pendingZoomRef.current, pendingPositionRef.current, stage);
        pendingZoomRef.current = null;
        pendingPositionRef.current = null;
    }, [updateAtoms]);
    /**
     * Сбрасывает состояние touch жеста
     */
    const resetTouchGestureState = useCallback(() => {
        touchStateRef.current.isPanning = false;
        touchStateRef.current.isZooming = false;
        touchStateRef.current.gestureType = null;
        if (touchStateRef.current.isZoomingFlagSet) {
            setIsZooming(false);
            touchStateRef.current.isZoomingFlagSet = false;
        }
        // Завершаем оптимизацию при сбросе жеста
        endFastPan();
    }, [setIsZooming]);
    /** Обработчик нативных touch событий */
    const handleTouchStart = useCallback((e) => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const touches = Array.from(e.evt.touches);
        const touchCount = touches.length;
        // Для одного пальца сначала проверяем, не элемент ли это
        // Это нужно сделать ДО любых изменений состояния
        if (touchCount === 1) {
            const target = e.target;
            // Проверяем как через target, так и через getIntersection для надежности
            const touchPoint = touches[0];
            const rect = getCachedContainerRect(stage);
            const pointerPos = {
                x: touchPoint.clientX - rect.left,
                y: touchPoint.clientY - rect.top,
            };
            const shape = stage.getIntersection(pointerPos);
            const isElement = (target && target !== stage && isElementShape(target)) ||
                (shape && isElementShape(shape));
            if (isElement) {
                // Touch на элементе - полностью пропускаем обработку для Konva drag
                // Не делаем НИЧЕГО - просто возвращаемся, чтобы Konva мог обработать событие
                return;
            }
        }
        // Только если это НЕ элемент (или multi-touch), обрабатываем дальше
        resetZoomFlag();
        touchStateRef.current.lastTouchCount = touchCount;
        if (touchCount === 2) {
            // Multi-touch жест - всегда обрабатываем pan/zoom
            e.evt.preventDefault();
            const pos1 = { x: touches[0].clientX, y: touches[0].clientY };
            const pos2 = { x: touches[1].clientX, y: touches[1].clientY };
            initializeMultiTouchGesture(stage, pos1, pos2);
        }
        else if (touchCount === 1) {
            // Touch на Stage - готовимся к возможному pan жесту (но не блокируем события)
            touchStateRef.current.isPanning = false;
            touchStateRef.current.isZooming = false;
        }
        else {
            // Больше 2 пальцев - блокируем стандартное поведение
            touchStateRef.current.isPanning = false;
            touchStateRef.current.isZooming = false;
            if (touchCount > 2) {
                e.evt.preventDefault();
            }
        }
    }, [store, resetZoomFlag, initializeMultiTouchGesture, getCachedContainerRect]);
    /**
     * Обработчик движения touch события
     * Оптимизирован для мобильных устройств: обновляет Stage напрямую, атомы батчит через RAF
     */
    const handleTouchMove = useCallback((e) => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const touches = Array.from(e.evt.touches);
        const touchCount = touches.length;
        if (touchCount === 1) {
            // Один палец - если touch на элементе, не обрабатываем (позволяем Konva обработать drag)
            const target = e.target;
            // Проверяем как через target, так и через getIntersection для надежности
            const touchPoint = touches[0];
            const rect = getCachedContainerRect(stage);
            const pointerPos = {
                x: touchPoint.clientX - rect.left,
                y: touchPoint.clientY - rect.top,
            };
            const shape = stage.getIntersection(pointerPos);
            const isElement = (target && target !== stage && isElementShape(target)) ||
                (shape && isElementShape(shape));
            if (isElement) {
                return;
            }
            // Для одного пальца на Stage ничего не делаем (панорамирование через drag Stage)
            return;
        }
        if (touchCount === 2 && touchStateRef.current.isPanning) {
            e.evt.preventDefault();
            const pos1 = { x: touches[0].clientX, y: touches[0].clientY };
            const pos2 = { x: touches[1].clientX, y: touches[1].clientY };
            const currentDistance = getDistance(pos1, pos2);
            const currentCenter = getCenter(pos1, pos2);
            const lastDistance = touchStateRef.current.lastDistance;
            const lastCenter = touchStateRef.current.lastCenter;
            const distanceChange = Math.abs(currentDistance - lastDistance);
            const centerChange = getDistance(currentCenter, lastCenter);
            const activeGestureType = detectGestureType(distanceChange, centerChange, touchStateRef.current.gestureType);
            if (activeGestureType === 'zoom') {
                touchStateRef.current.gestureType = 'zoom';
                const scaleFactor = currentDistance / lastDistance;
                handleTouchZoom(stage, scaleFactor, currentCenter);
                updateGestureState(pos1, pos2);
            }
            else if (activeGestureType === 'pan') {
                touchStateRef.current.gestureType = 'pan';
                const deltaX = currentCenter.x - lastCenter.x;
                const deltaY = currentCenter.y - lastCenter.y;
                handleTouchPan(stage, deltaX, deltaY);
                updateGestureState(pos1, pos2);
            }
            // Если жест не определен (маленькие изменения), не обновляем состояние
            // чтобы накопить изменения и правильно определить жест при следующем движении
        }
        else if (touchCount > 2) {
            e.evt.preventDefault();
        }
    }, [
        store,
        getDistance,
        getCenter,
        detectGestureType,
        handleTouchZoom,
        handleTouchPan,
        updateGestureState,
        getCachedContainerRect,
    ]);
    /**
     * Обработчик окончания touch события
     * Финализирует обновления атомов в конце жеста
     */
    const handleTouchEnd = useCallback((e) => {
        const touches = Array.from(e.evt.touches);
        const touchCount = touches.length;
        // Для одного пальца проверяем, не был ли это элемент (drag элемента)
        if (touchCount === 0 || touchCount === 1) {
            const changedTouches = Array.from(e.evt.changedTouches);
            if (changedTouches.length > 0) {
                const stage = store.get(stageRefAtom);
                if (stage) {
                    const touchPoint = changedTouches[0];
                    const rect = getCachedContainerRect(stage);
                    const pointerPos = {
                        x: touchPoint.clientX - rect.left,
                        y: touchPoint.clientY - rect.top,
                    };
                    const shape = stage.getIntersection(pointerPos);
                    // Если touch был на элементе - не обрабатываем (это был drag элемента)
                    if (shape && isElementShape(shape)) {
                        touchStateRef.current.lastTouchCount = touchCount;
                        return;
                    }
                }
            }
        }
        // Блокируем стандартное поведение только если был активен multi-touch жест
        if (touchStateRef.current.isPanning || touchStateRef.current.isZooming) {
            e.evt.preventDefault();
        }
        if (touchCount < 2) {
            resetTouchGestureState();
            const stage = store.get(stageRefAtom);
            if (stage && (pendingZoomRef.current !== null || pendingPositionRef.current !== null)) {
                finalizeAtomUpdates(stage);
            }
            touchStateRef.current.lastTouchCount = touchCount;
        }
    }, [store, resetTouchGestureState, finalizeAtomUpdates, getCachedContainerRect]);
    /**
     * Определяет тип wheel события
     */
    const getWheelEventType = useCallback((e) => {
        // Если режим принудительно установлен пользователем, используем его
        if (inputMode === 'mouse') {
            // В режиме мыши: колесико = зум, Ctrl+колесико = зум
            return 'mouse-zoom';
        }
        if (inputMode === 'trackpad') {
            // В режиме трекпада: движение = pan, Ctrl+движение = zoom, Meta+движение = zoom
            if (e.metaKey || e.ctrlKey)
                return 'trackpad-zoom';
            return 'trackpad-pan';
        }
        // Автоопределение (fallback на старую логику, если режим не установлен)
        const isDeltaFloat = !Number.isInteger(e.deltaY) || !Number.isInteger(e.deltaX);
        const isMouse = !isDeltaFloat &&
            os !== OSTypeEnum.Android &&
            os !== OSTypeEnum.iOS &&
            Math.abs(e.deltaX) === 0 &&
            Math.abs(e.deltaY) > MOUSE_ZOOM_THRESHOLD;
        // Если зажата CMD (Meta) - всегда зумирование
        if (e.metaKey)
            return isMouse ? 'mouse-zoom' : 'trackpad-zoom';
        // Если зажата Ctrl - всегда зумирование для трекпада
        if (e.ctrlKey)
            return 'trackpad-zoom';
        return isMouse ? 'mouse-zoom' : 'trackpad-pan';
    }, [os, inputMode]);
    /**
     * Обработчик wheel событий для трекпада и мыши (pan и zoom жесты)
     */
    const handleWheel = useCallback((e) => {
        e.evt.preventDefault();
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        const eventType = getWheelEventType(e.evt);
        switch (eventType) {
            case 'trackpad-pan':
                handleTrackpadPan(stage, e.evt.deltaX, e.evt.deltaY);
                break;
            case 'mouse-zoom': {
                const pointer = getPointerFromWheelEvent(stage, e);
                const direction = e.evt.deltaY > 0 ? -1 : 1;
                handleMouseZoom(stage, pointer, direction);
                break;
            }
            case 'trackpad-zoom': {
                const pointer = getPointerFromWheelEvent(stage, e);
                handleTrackpadZoom(stage, e.evt.deltaY, pointer);
                break;
            }
        }
    }, [
        store,
        getWheelEventType,
        handleTrackpadPan,
        handleMouseZoom,
        handleTrackpadZoom,
        getPointerFromWheelEvent,
    ]);
    return {
        onWheel: handleWheel,
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
    };
};
